// @flow

import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { selectCartUUID as selectCartId } from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import { Button } from '@gandi/ui';

import { doCommitCart } from 'src/Overview/modules/actions';
import { selectIsCheckoutButtonDisabled } from 'src/Overview/modules/selectors';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { ButtonContent } from './ButtonContent';

import styles from './Button.scss';

type Params = {
  cartId: string,
  orderId?: string,
};

type Props = {
  price: string,
};

export function CheckoutButton({ price }: Props): React.MixedElement {
  const translator = useTranslator();
  const { __ } = translator;
  const dispatch = useDispatch();

  const { orderId } = useParams<Params>();
  const disabled = useAppSelector(selectIsCheckoutButtonDisabled);
  const cartId = useAppSelector(selectCartId);
  const navigate = useNavigate();

  const handleClick = async (evt: SyntheticMouseEvent<>) => {
    evt.preventDefault();

    try {
      const response = await dispatch(doCommitCart());
      navigate(`/cart/${cartId}/checkout/${response.data.session_id}`);
    } catch (error) {
      const title = error?.response?.data.message || __('Cart checkout error');
      const message = error.errors
        ? error.errors.map(({ description }) => description)
        : __('An error occurred when committing your cart.');
      dispatch(
        notifyDanger({
          title,
          message,
        }),
      );
    }
  };

  return (
    <Button
      block
      key="cart-checkout"
      className={styles.button}
      to={orderId ? `/cart/${cartId}/checkout/${orderId}` : undefined}
      disabled={!orderId ? disabled : undefined}
      onClick={!orderId && !disabled ? handleClick : undefined}
    >
      <ButtonContent addon={price}>
        {__('<for-translators-use>Proceed to payment</for-translators-use>Pay')}
      </ButtonContent>
    </Button>
  );
}
